import React from 'react';
import { motion } from 'framer-motion';
import { BookBookmark, ShieldCheck } from '@phosphor-icons/react';

/**
 * SourceTrustStrip — premium trust marker shown beneath an assistant reply.
 *
 * Input: a `using_context` string composed by the backend (e.g. "Using your
 * Hashimoto's thyroiditis (7+ other conditions), Levothyroxine (+10 other
 * meds), your endocrinology 6-month review.")
 *
 * We parse the friendly sentence into individual source chips so the user
 * can *see* exactly what Kindra drew on — a clearer trust signal than a
 * block of italics. If parsing yields nothing, we fall back to the original
 * sentence so nothing is lost.
 */
const PREFIXES = [
  /^using your\s+/i,
  /^using what you just shared\s*[\.—-]?\s*/i,
  /^using general medical knowledge\s*—\s*not your personal record\s*\.?\s*/i,
  /^using\s+/i,
];

/**
 * Stage 20B+ Wave B — beautify a raw source chip label. Backend sometimes
 * includes a filename-ish fragment (e.g. "pathology_report_20240315.pdf") or
 * "+4 other conditions". This helper makes chips human-readable without
 * changing their meaning:
 *   • strips file extensions (.pdf, .jpg, .png, .heic)
 *   • converts underscores to spaces
 *   • reformats bare YYYYMMDD dates into "15 March 2024"
 *   • leaves already-clean labels untouched
 */
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const beautifyLabel = (raw) => {
  if (!raw) return raw;
  let s = String(raw).trim();

  // Strip common file extensions, case-insensitive
  s = s.replace(/\.(pdf|PDF|jpe?g|JPE?G|png|PNG|heic|HEIC|docx?|DOCX?)\b/g, '');

  // Replace YYYYMMDD or YYYY-MM-DD with "D Mon YYYY"
  s = s.replace(/(\d{4})[-_]?(\d{2})[-_]?(\d{2})/g, (_, y, m, d) => {
    const monIdx = Number(m) - 1;
    if (monIdx < 0 || monIdx > 11) return `${d} ${m} ${y}`;
    return `${Number(d)} ${MONTH_NAMES[monIdx]} ${y}`;
  });

  // Underscores → spaces, collapse whitespace
  s = s.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();

  // Title-case only the first word if the label is all lowercase (keeps
  // "7+ other conditions" and "your endocrinology 6-month review" untouched).
  if (s && s === s.toLowerCase()) {
    s = s.charAt(0).toUpperCase() + s.slice(1);
  }
  return s;
};

/**
 * Parses the backend's `using_context` sentence into:
 *   - `prefix`: a short bold-label ("Based on" / "Drawing from" / "No personal record")
 *   - `sources`: 0–4 chips representing the actual sources Kindra used
 *   - `mode`: 'general' (no record), 'recent' (just-shared), 'record' (chips)
 */
function parseSources(text = '') {
  if (!text || typeof text !== 'string') return { prefix: null, sources: [], fallback: text };
  const s = text.trim();
  const lower = s.toLowerCase();

  // Mode 1: Kindra explicitly told us it has NO personal record.
  if (/general medical knowledge/.test(lower) || /not your personal record/.test(lower)) {
    return {
      prefix:   'General medical knowledge',
      sources:  [],
      fallback: 'General health information — not from your record.',
      mode:     'general',
    };
  }

  // Mode 2: Just-now context (user shared something this turn but has no
  // standing record yet).
  if (/just shared|what you just told/.test(lower)) {
    return {
      prefix:   'Based on',
      sources:  ['What you just shared'],
      fallback: s,
      mode:     'recent',
    };
  }

  // Mode 3: Standing record. Strip the "Using your" preamble and split.
  let body = s;
  for (const re of PREFIXES) {
    if (re.test(body)) {
      body = body.replace(re, '').trim();
      break;
    }
  }
  body = body.replace(/[\.。]+\s*$/, '');
  const rough = body
    .split(/,\s+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(beautifyLabel)                                // Stage 20B+ Wave B
    .filter(Boolean)
    .slice(0, 4);
  return {
    prefix:   'Based on your record',
    sources:  rough,
    fallback: text,
    mode:     'record',
  };
}

export const SourceTrustStrip = ({ text }) => {
  if (!text) return null;
  const parsed = parseSources(text);

  // Fallback: if we couldn't extract clean sources, render the sentence quietly.
  if (!parsed.sources.length) {
    return (
      <div
        className="mt-2 text-[11.5px] italic leading-relaxed text-slate-500 max-w-prose inline-flex items-start gap-1.5"
        data-testid="chat-using-context"
      >
        <ShieldCheck size={11} weight="duotone" className="text-[#2FB3A0] flex-shrink-0 mt-[2px]" />
        <span>{parsed.fallback}</span>
      </div>
    );
  }

  return (
    <div
      className="mt-2.5 max-w-prose"
      data-testid="chat-using-context"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <BookBookmark size={11} weight="duotone" className="text-[#2FB3A0]" />
        <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          {parsed.prefix || 'Kindra used'}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {parsed.sources.map((src, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.04 * i }}
            className="inline-flex items-center gap-1 rounded-md bg-[#2FB3A0]/[0.08] border border-[#2FB3A0]/20 px-2 py-0.5 text-[11px] text-[#0E1C2E] leading-tight"
            data-testid={`chat-source-chip-${i}`}
          >
            {src}
          </motion.span>
        ))}
      </div>
    </div>
  );
};

export default SourceTrustStrip;
